import { batch, signal } from '@preact/signals'
import type { Client } from '../../../client/protocol.ts'
import { windowName } from '../../../client/format.ts'
import type { AccountView, SessionsParams } from '../../../protocol/views.ts'
import { PageOwner } from '../shell/owner.ts'
import { watch } from '../store/watch.ts'
import { createDisplay } from './display.ts'
import { duration } from '../ui/tokens.ts'
import type { BackendPage } from './page.ts'

export interface OverviewOptions { sidebar?: boolean; header?: boolean }
export interface OverviewCounts { workspace?: number; needs?: number; working?: number }
export interface QuotaGlance { text: string; title: string; left: number }
const backends: BackendPage[] = ['all', 'claude', 'codex', 'agy']
const services = new WeakMap<Client, ReturnType<typeof createOverview>>()

/** A glance uses an observed current account and native primary window, never an estimated allowance. */
export function quotaGlance(accounts: readonly AccountView[], now = Date.now()): QuotaGlance | undefined {
  const account = accounts.find(account => account.backend === 'codex' && account.current)
  if (!account?.quota || account.quota.error || account.quota.unknown || account.state === 'unavailable') return
  const buckets = account.quota.buckets ?? []
  const candidates = buckets.some(bucket => bucket.limitId === 'codex') ? buckets.filter(bucket => bucket.limitId === 'codex') : buckets
  const bucket = candidates.filter(bucket => bucket.primary?.usedPercent !== undefined && Number.isFinite(bucket.primary.usedPercent) && bucket.primary.usedPercent >= 0 && bucket.primary.usedPercent <= 100
      && (bucket.primary.resetsAt === undefined || Number.isFinite(Date.parse(bucket.primary.resetsAt)) && Date.parse(bucket.primary.resetsAt) > now))
    .sort((a, b) => b.primary!.usedPercent! - a.primary!.usedPercent!)[0]
  const primary = bucket?.primary
  if (!primary || primary.usedPercent === undefined || !Number.isFinite(primary.usedPercent) || primary.usedPercent < 0 || primary.usedPercent > 100) return
  const left = 100 - primary.usedPercent
  if (left > 30) return
  return { left, text: `${windowName(primary.windowMinutes)} ${Math.round(left)}% left`, title: `${account.label} · ${bucket.name ?? bucket.limitId} · Source: ${account.quota.source} · ${account.quota.at}` }
}

function createOverview(client: Client) {
  const counts = signal<Record<BackendPage, OverviewCounts>>({ all: {}, claude: {}, codex: {}, agy: {} })
  const preview = createDisplay({ tapGuard: duration['tap-guard'] }), quota = signal<QuotaGlance | undefined>(undefined)
  const error = signal<string | undefined>(undefined)
  const consumers = new Map<PageOwner, OverviewOptions>()
  type Demand = { backend: BackendPage; field: keyof OverviewCounts; params: SessionsParams; preview?: boolean }
  type ViewRecord = { owner: PageOwner; key: string; demand?: Demand; ready: boolean; failed: boolean }
  const records = new Map<string, ViewRecord>()
  let disposed = false, unsubscribe = () => {}, lifecycle = () => {}
  const reportError = () => { error.value = [...records.values()].some(record => record.failed) ? 'Couldn’t load the session summary.' : undefined }
  const setCount = (backend: BackendPage, field: keyof OverviewCounts, value: number | undefined) => {
    const old = counts.peek()
    if (old[backend][field] !== value) counts.value = { ...old, [backend]: { ...old[backend], [field]: value } }
  }
  const clear = (record: ViewRecord) => {
    record.ready = false
    if (record.demand) setCount(record.demand.backend, record.demand.field, undefined)
    else quota.value = undefined
  }
  const close = (name: string) => {
    const record = records.get(name)
    if (!record) return
    records.delete(name); record.owner.dispose(); clear(record); reportError()
  }
  function reconcile() {
    if (disposed) return
    const active = [...consumers].filter(([owner]) => owner.active && !owner.disposed)
    const header = active.some(([, options]) => options.header), sidebar = active.some(([, options]) => options.sidebar)
    const wanted = new Map<string, Demand | undefined>()
    const demand = (backend: BackendPage, field: keyof OverviewCounts, filter?: SessionsParams['filter'], limit = 1, preview?: boolean) => {
      wanted.set(`${backend}:${field}`, { backend, field, preview, params: { archived: false, includeChildren: false, ...(backend === 'all' ? {} : { backend }), ...(filter ? { filter } : {}), limit } })
    }
    if (active.length) demand('all', 'needs', 'needs-you', sidebar ? 5 : 1, sidebar)
    if (header) {
      for (const backend of backends) {
        demand(backend, 'workspace')
        if (backend !== 'all') demand(backend, 'needs', 'needs-you')
      }
      wanted.set('accounts', undefined)
    }
    if (header || sidebar) demand('all', 'working', 'running-unattended', sidebar ? 3 : 1, sidebar)
    batch(() => {
      for (const [name, record] of records) if (!wanted.has(name) || record.key !== JSON.stringify(wanted.get(name)?.params)) close(name)
      for (const [name, demand] of wanted) {
        if (records.has(name)) continue
        const record: ViewRecord = { owner: new PageOwner(), key: JSON.stringify(demand?.params), demand, ready: false, failed: false }
        records.set(name, record)
        const live = () => records.get(name) === record && !record.owner.disposed && !disposed
        const failed = () => { if (live()) { record.failed = true; clear(record); reportError() } }
        if (!demand) {
          const update = (accounts: AccountView[]) => {
            if (!live()) return
            record.ready = true
            const next = quotaGlance(accounts), old = quota.peek()
            if (old?.text !== next?.text || old?.title !== next?.title || old?.left !== next?.left) quota.value = next
          }
          watch(record.owner, client, 'accounts', {}, { onSnapshot: data => update(data.accounts), onPatch: ops => { if (record.ready) for (const op of ops) update(op.accounts) }, onReset: () => { if (live()) clear(record) }, onError: failed })
        } else {
          const group = demand.field === 'needs' ? 'needs-you' as const : 'running-unattended' as const, source = `${group}:page:0`
          watch(record.owner, client, 'sessions', demand.params, {
            onSnapshot: data => {
              if (!live()) return
              batch(() => {
                record.ready = true; setCount(demand.backend, demand.field, data.total)
                if (demand.preview) preview.dispatch({ type: 'snapshot', source, group, data })
              })
            },
            onPatch: ops => {
              if (!live() || !record.ready) return
              batch(() => { for (const op of ops) if (op.op === 'total') setCount(demand.backend, demand.field, op.total); if (demand.preview) preview.dispatch({ type: 'patch', source, ops }) })
            },
            onReset: () => { if (live()) clear(record) }, onError: failed,
          })
        }
      }
    })
  }
  const dispose = () => {
    if (disposed) return
    disposed = true; unsubscribe(); lifecycle()
    batch(() => { for (const name of records.keys()) close(name); preview.dispose() })
    consumers.clear(); services.delete(client)
  }
  unsubscribe = client.subscribe(state => { if (state !== 'online') batch(() => { for (const record of records.values()) clear(record) }) })
  lifecycle = client.subscribeLifecycle(event => { if (event === 'disposed') dispose() })
  return {
    counts, preview, quota, error,
    acquire(owner: PageOwner, options: OverviewOptions) {
      if (!disposed && !owner.disposed) {
        consumers.set(owner, options)
        const activity = owner.onActive(reconcile)
        owner.own(() => { activity(); consumers.delete(owner); reconcile(); if (!consumers.size) dispose() })
        reconcile()
      }
      return { counts, preview, quota, error,
        update(next: OverviewOptions) { if (consumers.has(owner)) { consumers.set(owner, next); reconcile() } },
        retry() { batch(() => { for (const [name, record] of records) if (record.failed) close(name); reconcile() }) },
      }
    },
  }
}

/** Shell and Sessions share one aggregate projection and release it with their visible owners. */
export function acquireOverview(client: Client, owner: PageOwner, options: OverviewOptions = {}) {
  let service = services.get(client)
  if (!service) { service = createOverview(client); services.set(client, service) }
  return service.acquire(owner, options)
}
export type SessionsOverview = ReturnType<typeof acquireOverview>
